import { useMemo } from 'react';

import { PrivilegeKey } from './access/access-table';
import { useAuth } from './use-auth';
import { usePrivilegeAccessControl } from './use-privilege-access-control';

interface UseActionPermissionProps {
  privilege: PrivilegeKey;
}

type PermissionReason = 'no-access' | 'view-only' | null;

interface UseActionPermissionResult {
  isAllowed: boolean;
  isDisabled: boolean;
  tooltipText: string | undefined;
  reason: PermissionReason;
  isViewOnly: boolean;
  hasPrivilege: boolean;
}

export const useActionPermission = ({
  privilege,
}: UseActionPermissionProps): UseActionPermissionResult => {
  const { isViewOnly } = useAuth();
  const { data: access } = usePrivilegeAccessControl();

  const hasPrivilege = access?.[privilege] ?? false;
  const isAllowed = !isViewOnly && hasPrivilege;

  const reason = useMemo((): PermissionReason => {
    if (!hasPrivilege) return 'no-access';
    if (isViewOnly) return 'view-only';
    return null;
  }, [hasPrivilege, isViewOnly]);

  const tooltipText = useMemo(() => {
    if (!hasPrivilege) return 'You do not have access.';
    if (isViewOnly) return 'You are in View Only mode, go active first';
    return undefined;
  }, [hasPrivilege, isViewOnly]);

  return {
    isAllowed,
    isDisabled: !isAllowed,
    tooltipText,
    reason,
    isViewOnly,
    hasPrivilege,
  };
};
